import React, { useEffect, useState } from 'react';
import CustomSwitch from '../../common/CustomSwitch';
import VerificationModal from './VerificationModal';
import qrcode from 'qrcode';
import { apiService } from '../../../../client/common/apiCallService';
import { useSelector } from 'react-redux';
import { useJunoCommonFunctions } from '../../../../client/Juno/helpers';

const SecuritySettings = ({ isMobile, onClose: onParentClose }) => {
    const { getSecretKey, getClientData } = useJunoCommonFunctions();

    const API_URL = useSelector(state => state.config.api_url);
    const clientData = useSelector(state => state.juno.clientData);

    const [isActivated, setIsActivated] = useState(false);
    const [verificationModalOpen, setVerificationModalOpen] = useState(false);
    const [secret, setSecret] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [qrCodeData, setQrCodeData] = useState(null);

    const [verifyPasswordModalOpen, setVerifyPasswordModalOpen] = useState(false);
    const [password, setPassword] = useState('');
    
    useEffect(() => {
        if (clientData) {
            setIsActivated(clientData.twoFA.enabled);
        }
    }, [clientData]);

    useEffect(() => {
        if (isMobile) {
            handleToggle2FA();
        }
    }, []);

    useEffect(() => {
        if (secret) {
            save2FASetting();
        }
    }, [secret]);

    // Handler for switch toggle
    const handleToggle2FA = async () => {
        setVerifyPasswordModalOpen(true);
        setVerificationModalOpen(true); 
        setError('');
    };

    const handleProceedToVerification = async () => {
        setVerifyPasswordModalOpen(false);
        
        if (!isActivated) {
            const data = await getSecretKey();
            if (data) {
                setSecret(data);
            }
            setVerificationModalOpen(true);
        } else {
            setVerificationModalOpen(false); 
            getClientData(); 
            if (onParentClose) {
                onParentClose();
            }
        }
    };

    const handleCloseTwoStepModal = () => {
        setVerifyPasswordModalOpen(false);
        if (isMobile && onParentClose) {
            onParentClose(); 
        }
    };

    const handleCloseVerificationModal = () => {
        setVerificationModalOpen(false);
        setInputValue('');
        setError('');
        setPassword('')
        if (isMobile && onParentClose) {
            onParentClose(); 
        }
    };

    // Input change handler
    function onInputChange(val) {
        setError('');
        setInputValue(val);
    }

    // 2FA activation/deactivation handler
    async function changeActivation() {
        if (inputValue.length === 6) {
            setIsLoading(true);
            if (!isActivated) {
                apiService(
                    (window.location.hostname === 'localhost' ? API_URL : '') +
                        '/restapi/client-save-2fa-secret',
                    {
                        secret: secret?.base32,
                        encoding: 'base32',
                    },
                    data => {
                        setInputValue('');
                    },
                    err => {
                        setIsLoading(false);
                        console.error(err);
                    },
                );
            }

            // Verify token and change activation status
            apiService(
                (window.location.hostname === 'localhost' ? API_URL : '') +
                    '/restapi/client-verify-2fa-token',
                {
                    user2faToken: inputValue,
                    changeActivation: 'activate',
                    user_email: clientData?.clientEmail,
                },
                data => {
                    setIsLoading(false);
                    if (data.success) {
                        setVerificationModalOpen(false);
                        setIsActivated(!isActivated);
                        setInputValue('');
                        getClientData();

                        if (onParentClose) {
                            onParentClose();
                        }
                    } else {
                        setError('Invalid verification token!');
                    }
                },
                err => {
                    setIsLoading(false);
                    console.error(err);
                },
            );
        } else {
            setError('Invalid token! 2FA token must have 6-digit only');
        }
    }

    // Generate and save QR code
    const save2FASetting = async () => {
        qrcode.toDataURL(secret?.otpauth_url, function (err, imgData) {
            if (imgData) {
                setQrCodeData(imgData);
            }
            if (err) {
                console.error(err);
            }
        });

        setIsLoading(true);
        apiService(
            (window.location.hostname === 'localhost' ? API_URL : '') +
                '/restapi/client-save-2fa-secret',
            {
                secret: secret?.base32,
                encoding: 'base32',
            },
            data => {
                setIsLoading(false);
                if (data.success) {
                    setError('');
                }
            },
            err => {
                setIsLoading(false);
                console.error(err);
            },
        );
    };

    const handlePasswordCheck = async() => {
            if (!isActivated && !password.trim()) {
                handleProceedToVerification();
                return;
            }
    
            setIsLoading(true);
            setError('');
    
            const payload = {
                clientEmail: clientData?.clientEmail,
                password: password,
            };
    
            if (isActivated) {
                payload.disableTwoFA = true;
            }
    
            apiService(
                (window.location.hostname === 'localhost' ? API_URL : '') +
                    '/restapi/verify-client-password    ',
                payload,
                data => {
                    setIsLoading(false);
                    if (data && data.success) {
                        handleProceedToVerification();
                        setPassword('');
                    } else {
                        setError('Invalid password. Please try again.');
                    }
                },
                err => {
                    setIsLoading(false);
                    console.error('Password verification failed:', err);
                    setError('Password verification failed. Please try again.');
                },
            );
        }
    

    return (
        <>
            {!isMobile && (
                <>
                    <p className="px-6 py-4 text-profile-primary text-sm font-semibold leading-5">
                        SECURITY SETTINGS
                    </p>

                    <div
                        className="flex justify-between items-center !p-5 self-stretch rounded-md bg-card-bg mx-6 mt-3 mb-6"
                        data-e2e="receive-transctions-emails">
                        <p className="text-text text-sm font-semibold leading-5 uppercase">
                            Two factor log in authentication (2fa) - high security
                        </p>

                        <CustomSwitch
                            sx={{ m: 1 }}
                            onChange={handleToggle2FA}
                            checked={isActivated}
                        />
                    </div>
                </>
            )}

           
            {/* Verification Modal */}
            <VerificationModal
                open={verificationModalOpen}
                onClose={handleCloseVerificationModal}
                changeActivation={changeActivation}
                isActivated={isActivated}
                qrCodeData={qrCodeData}
                error={error}
                inputValue={inputValue}
                setInputValue={onInputChange}
                isLoading={isLoading}
                secret={secret}
                setError={setError}

                verifyPasswordModalOpen={verifyPasswordModalOpen}
                password={password}
                setPassword={setPassword}
                handlePasswordCheck={handlePasswordCheck}
            />
        </>
    );
};

export default SecuritySettings;